import React from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import { LockIcon, MessageSquareIcon, PinIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { Container } from '../../components/ui/Primitives';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { Seo } from '../../components/Seo';
import { JoinDialog } from '../../components/academy/JoinDialog';
import { UpvoteButton } from '../../components/academy/UpvoteButton';
import { GENERAL_CHANNELS } from '../../data/academyCommunity';
import { useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
import { timeAgo } from '../../utils/academyFormat';
import type { ForumChannel } from '../../types/academyHub';
type Sort = 'recent' | 'popular';
export function AcademyCommunity() {
  const {
    channel: channelSlug
  } = useParams<{
    channel?: string;
  }>();
  const navigate = useNavigate();
  const {
    state
  } = useAcademy();
  const {
    hub,
    role,
    createThread,
    channels
  } = useAcademyHub();
  const [sort, setSort] = React.useState<Sort>('recent');
  const [query, setQuery] = React.useState('');
  const [composing, setComposing] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [joinOpen, setJoinOpen] = React.useState(false);
  const staff = role !== 'learner';
  const canSee = (c: ForumChannel) => c.type === 'general' || staff || Boolean(c.courseSlug && state.enrolments[c.courseSlug]);
  const myCourseChannels = channels.filter(c => c.type === 'course' && canSee(c));
  const channel = channelSlug ? channels.find(c => c.slug === channelSlug) : undefined;
  const visibleSlugs = new Set([...GENERAL_CHANNELS, ...myCourseChannels].map(c => c.slug));
  const locked = channel ? !canSee(channel) : false;
  const q = query.trim().toLowerCase();
  const replyCount = (id: string) => hub.replies.filter(r => r.threadId === id && !r.hidden).length;
  const threads = hub.threads.filter(t => !t.hidden && (channel ? t.channel === channel.slug : visibleSlugs.has(t.channel))).filter(t => !q || t.title.toLowerCase().includes(q) || t.body.toLowerCase().includes(q)).sort((a, b) => {
    if (Boolean(b.pinned) !== Boolean(a.pinned)) return Number(Boolean(b.pinned)) - Number(Boolean(a.pinned));
    return sort === 'popular' ? b.upvotes - a.upvotes : b.createdAt.localeCompare(a.createdAt);
  });
  const startCompose = () => {
    if (!state.learner) setJoinOpen(true);else setComposing(true);
  };
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (title.trim().length < 6) next.title = 'Give your thread a title of at least six characters.';
    if (body.trim().length < 10) next.body = 'Add a little more detail so others can help.';
    setErrors(next);
    if (Object.keys(next).length || !channel) return;
    const id = createThread(channel.slug, title, body);
    setTitle('');
    setBody('');
    setComposing(false);
    navigate(`/academy/community/${channel.slug}/${id}`);
  };
  return <>
      <Seo title={`${channel ? channel.name : 'Community'}, Cavalla Academy`} description="Discussion for Cavalla Academy learners." path="/academy/community" noIndex data-id="element-1578" data-mp="37gord8fdxsk5" />
      <Container className="grid gap-10 py-10 sm:py-14 lg:grid-cols-[240px_minmax(0,1fr)]" data-id="element-1579" data-mp="2xr54lrojxek0">
        <aside aria-label="Channels" className="lg:sticky lg:top-28 lg:self-start" data-id="element-1580" data-mp="32i55f80skgrs">
          <ChannelGroup title="General" channels={GENERAL_CHANNELS} data-id="element-1581" data-mp="2oqyy4pr9teuc" />
          <div className="mt-8" data-id="element-1582" data-mp="2osth3yizewvs">
            <ChannelGroup title="Your course channels" channels={myCourseChannels} data-id="element-1583" data-mp="2tptpig3b0bq2" />
            {!myCourseChannels.length && <p className="mt-2 text-small text-muted" data-id="element-1584" data-mp="2etu82eub0z89">Each course you enrol in opens its own channel here.</p>}
          </div>
          <NavLink to="/academy/community" end className="mt-8 block text-small text-royal underline underline-offset-4 hover:text-ink" data-id="element-1585" data-mp="2ju0384zs93v3">
            All discussions
          </NavLink>
        </aside>

        <div className="min-w-0" data-id="element-1586" data-mp="3i5t6vhyblg2m">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-1587" data-mp="3dcgkleh45ld1">
            <div data-id="element-1588" data-mp="38e6n2x7x28o9">
              <h1 className="font-display text-[28px] leading-tight text-ink sm:text-[34px]" data-id="element-1589" data-mp="38eosajck3x8b">{channel ? channel.name : 'Community'}</h1>
              <p className="mt-2 text-[15px] text-body" data-id="element-1590" data-mp="33g56qk1vkngh">
                {channel ? channel.description : 'Questions, answers and introductions from learners across the Academy.'}
              </p>
            </div>
            {channel && !locked && <Button type="button" onClick={startCompose} className="shrink-0" data-id="element-1591" data-mp="2pfkkfdwr49i8">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1592" data-mp="smr9ncjj6jpm" />
                New thread
              </Button>}
          </div>

          {locked ? <div className="mt-10 rounded-md border border-line bg-panel px-6 py-12 text-center" data-id="element-1593" data-mp="nohdozpzff2y">
              <LockIcon className="mx-auto h-5 w-5 text-gold" aria-hidden="true" data-id="element-1594" data-mp="v6xcti0bm654" />
              <p className="mt-3 font-display text-[20px] text-ink" data-id="element-1595" data-mp="104pc4x3aoujc">This channel is for enrolled learners</p>
              <Button to={`/academy/courses/${channel?.courseSlug}`} variant="secondary" className="mt-5" data-id="element-1596" data-mp="14xs8fczgii38">
                See the course
              </Button>
            </div> : <>
              {composing && channel && <form onSubmit={submit} noValidate className="mt-8 space-y-4 rounded-md border border-line bg-panel p-5 sm:p-6" data-id="element-1597" data-mp="h6wcq4rvr2qp">
                  <Field id="thread-title" label="Title" required error={errors.title} data-id="element-1598" data-mp="m5pl5isb83k0">
                    <input id="thread-title" value={title} maxLength={140} onChange={e => setTitle(e.target.value)} className={inputClass} data-id="element-1599" data-mp="r6n808o9llru" />
                  </Field>
                  <Field id="thread-body" label="Your question or post" required error={errors.body} data-id="element-1600" data-mp="22kin5hf65uj2">
                    <textarea id="thread-body" rows={5} value={body} onChange={e => setBody(e.target.value)} className={inputClass} data-id="element-1601" data-mp="2hztenxs8h2v" />
                  </Field>
                  <div className="flex justify-end gap-3" data-id="element-1602" data-mp="3tqwttwfj4r1c">
                    <Button type="button" variant="secondary" onClick={() => setComposing(false)} data-id="element-1603" data-mp="bi64y49x76ze">
                      Cancel
                    </Button>
                    <Button type="submit" data-id="element-1604" data-mp="3fxfc8h6elody">Post thread</Button>
                  </div>
                </form>}

              <div className="mt-8 flex flex-col gap-3 border-b border-line pb-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1605" data-mp="3ayukz794qf1n">
                <div className="relative sm:w-72" data-id="element-1606" data-mp="3oqiv54ztitfz">
                  <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-1607" data-mp="3jz9ayjx1gibh" />
                  <label htmlFor="forum-search" className="sr-only" data-id="element-1608" data-mp="2w83sry3mldy0">
                    Search discussions
                  </label>
                  <input id="forum-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search discussions" className={`${inputClass} py-2 pl-9 text-[14px]`} data-id="element-1609" data-mp="2s60hb9gyi9cq" />
                </div>
                <div role="tablist" aria-label="Sort" className="flex gap-1 rounded-full border border-line p-1" data-id="element-1610" data-mp="2d5l8ef77em26">
                  {(['recent', 'popular'] as Sort[]).map(s => <button key={s} type="button" role="tab" aria-selected={sort === s} onClick={() => setSort(s)} className={`rounded-full px-4 py-1.5 text-[13px] capitalize transition-colors duration-150 ${sort === s ? 'bg-gold text-charcoal' : 'text-body hover:text-ink'}`} data-id="element-1611" data-mp="35tqvh8lqcn56">
                      {s}
                    </button>)}
                </div>
              </div>

              {threads.length ? <ul className="divide-y divide-line" data-id="element-1612" data-mp="woxlb176s4qa">
                  {threads.map(t => {
              const ch = channels.find(c => c.slug === t.channel);
              return <li key={t.id} className="flex gap-4 py-5" data-id="element-1613" data-mp="nrexjp25rcap">
                        <UpvoteButton postId={t.id} count={t.upvotes} data-id="element-1614" data-mp="1kgcqr2u9f25r" />
                        <div className="min-w-0 flex-1" data-id="element-1615" data-mp="1fh9vga430aaz">
                          <h2 className="text-[16px] font-medium leading-snug text-ink" data-id="element-1616" data-mp="1fhjk9ude4bf3">
                            {t.pinned && <PinIcon className="mr-1.5 inline h-3.5 w-3.5 -translate-y-px text-gold" aria-label="Pinned" data-id="element-1617" data-mp="1aogrvpkcn80h" />}
                            <Link to={`/academy/community/${t.channel}/${t.id}`} className="hover:text-royal" data-id="element-1618" data-mp="16mvfe5bq8vqt">
                              {t.title}
                            </Link>
                          </h2>
                          <p className="mt-1 line-clamp-2 text-[14px] text-body" data-id="element-1619" data-mp="3h2vsn6unz76a">{t.body}</p>
                          <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-small text-muted" data-id="element-1620" data-mp="3m1p30qenmi28">
                            <span className={t.author.staff ? 'text-royal' : ''} data-id="element-1621" data-mp="3q2skfhccom5k">{t.author.name}</span>
                            <span data-id="element-1622" data-mp="3v2wv2qqkumnv">{timeAgo(t.createdAt)}</span>
                            {!channel && ch && <span className="truncate" data-id="element-1623" data-mp="3wc5lbg67msy">in {ch.name}</span>}
                            <span className="inline-flex items-center gap-1" data-id="element-1624" data-mp="3tzqd61srqjl">
                              <MessageSquareIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1625" data-mp="8tm4dhpg9atg" />
                              {replyCount(t.id)}
                            </span>
                          </p>
                        </div>
                      </li>;
            })}
                </ul> : <div className="py-14 text-center" data-id="element-1626" data-mp="rlof6cfetwvs">
                  <p className="font-display text-[19px] text-ink" data-id="element-1627" data-mp="rm6ghngxj1ak">{q ? 'No discussions match that search' : 'No threads yet'}</p>
                  <p className="mt-2 text-[14px] text-body" data-id="element-1628" data-mp="xauu2avn7ddx">{channel ? 'Start the first one.' : 'Pick a channel to start a thread.'}</p>
                </div>}
            </>}
        </div>
      </Container>
      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} onJoined={() => setJoinOpen(false)} data-id="element-1629" data-mp="1b4m4dlh771ap" />
    </>;
}
function ChannelGroup({
  title,
  channels
}: {
  title: string;
  channels: ForumChannel[];
}) {
  return <div data-id="element-1630" data-mp="1l17ayb8v8kgn">
      <h2 className="text-small font-medium text-muted" data-id="element-1631" data-mp="1l0yte61ssqz4">{title}</h2>
      <ul className="mt-2 space-y-0.5" data-id="element-1632" data-mp="1q0toklslw2pz">
        {channels.map(c => <li key={c.slug} data-id="element-1633" data-mp="1u2ydx8c0ud6t">
            <NavLink to={`/academy/community/${c.slug}`} className={({
          isActive
        }) => `block truncate rounded-sm px-3 py-2 text-[14px] transition-colors duration-150 ${isActive ? 'bg-panel text-ink' : 'text-body hover:bg-panel hover:text-ink'}`} data-id="element-1634" data-mp="1u385h0cpssem">
              {c.type === 'general' ? `# ${c.name}` : c.name}
            </NavLink>
          </li>)}
      </ul>
    </div>;
}